'use client';

import { c as _c } from "react/compiler-runtime";
import { useCouponSettings } from '@svs/nova-poolbetting/core/frameworks/nextjs/coupon';
import { useDraw } from '@svs/nova-poolbetting/core/frameworks/nextjs/draw';
import type { DrawData, DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { SettingsKeys } from '@svs/nova-poolbetting/schema/sessionstore';
import type { EventNumber, EventStatisticsKey } from '@svs/nova-poolbetting/schema/sport';
import { cn } from '@svs/nova-poolbetting/ui/utils';
import type { ReactNode } from 'react';
import { getMatchStartTime } from './getMatchStartTime';
import { TipsinfoValues } from './TipsinfoValues';
type TipsinfoValuesContainerProps = {
  settingsKey: SettingsKeys;
  drawNumber: DrawNumber;
  eventTypeStatisticsId?: EventStatisticsKey;
  eventNumber: EventNumber;
  product: Definition['moduleEngine'];
  className?: string;
};
const getEventTypeStatisticsId = (drawData: DrawData, eventNumber: EventNumber): EventStatisticsKey | undefined => {
  const drawEvent = drawData.draw.events.find(event => event.eventNumber === eventNumber);
  if (!drawEvent || drawEvent.status !== 'COMPLETE') {
    return undefined;
  }
  const eventTypeStatisticsId = drawData.bettingEvent[drawEvent.bettingEventId]?.eventTypeStatisticsId;
  return eventTypeStatisticsId;
};

/**
 * Tipsinfo values container component.
 *
 * @param root0 props
 * @param root0.drawNumber draw number to get tipsinfo values for
 * @param root0.settingsKey settings key to get tipsinfo values for eg. My bets or Play
 * @param root0.eventTypeStatisticsId event type statistics id to get tipsinfo values for
 * @param root0.eventNumber event number to get tipsinfo values for
 * @param root0.product product to get tipsinfo values for
 * @param root0.className optional class name for styling
 * @returns tipsinfo values component
 */
export function TipsinfoValuesContainer(t0) {
  const $ = _c(21);
  const {
    settingsKey,
    drawNumber,
    eventTypeStatisticsId,
    eventNumber,
    product,
    className
  } = t0;
  let t1;
  if ($[0] !== drawNumber || $[1] !== product) {
    t1 = {
      product,
      drawNumber
    };
    $[0] = drawNumber;
    $[1] = product;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const draw = useDraw(t1);
  const settings = useCouponSettings(product, settingsKey);
  if (draw.isError || settings.isError) {
    return;
  }
  const isOnlySvenskaFolketSelectedAndShownOnButtons = settings.data.tipsinfo.length === 1 && settings.data.tipsinfo[0] === "SvenskaFolket" && settings.data.outcomeLabel === "LocalDistribution";
  const hasInfoToDisplay = settings.data.tipsinfo.length > 0 && !isOnlySvenskaFolketSelectedAndShownOnButtons || settings.data.gameAnalysis.includes("Tip");
  if (!hasInfoToDisplay) {
    return;
  }
  const eventTypeStatisticsIdToUse = eventTypeStatisticsId ?? getEventTypeStatisticsId(draw.data, eventNumber);
  const statistics = eventTypeStatisticsIdToUse ? draw.data.eventTypeStatistics[eventTypeStatisticsIdToUse] : undefined;
  if (!statistics) {
    return;
  }
  let t2;
  if ($[3] !== draw.data || $[4] !== eventNumber) {
    t2 = getMatchStartTime(draw.data, eventNumber);
    $[3] = draw.data;
    $[4] = eventNumber;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const matchStart = t2;
  const sportType = draw.data.sport.type;
  let t3;
  if ($[6] !== draw.data.draw.events || $[7] !== eventNumber) {
    t3 = draw.data.draw.events.find(event => event.eventNumber === eventNumber)?.participantType;
    $[6] = draw.data.draw.events;
    $[7] = eventNumber;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const participantType = t3;
  if (!participantType) {
    return;
  }
  let t4;
  if ($[9] !== className || $[10] !== participantType) {
    t4 = participantType === "Player" ? cn("mt-7", className) : className;
    $[9] = className;
    $[10] = participantType;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== drawNumber || $[13] !== eventNumber || $[14] !== matchStart || $[15] !== product || $[16] !== settings.data || $[17] !== sportType || $[18] !== statistics || $[19] !== t4) {
    t5 = <TipsinfoValues eventStatistics={statistics} settings={settings.data} matchStart={matchStart} product={product} drawNumber={drawNumber} eventNumber={eventNumber} sportType={sportType} className={t4} />;
    $[12] = drawNumber;
    $[13] = eventNumber;
    $[14] = matchStart;
    $[15] = product;
    $[16] = settings.data;
    $[17] = sportType;
    $[18] = statistics;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
}